import React, { useEffect, useRef, useState } from 'react';
import { Award, Briefcase, Code2, Users, Calendar, Globe, Sparkles, Terminal } from 'lucide-react';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSkill, setActiveSkill] = useState(0);
  const sectionRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-rotate skills
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSkill((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { icon: Award, label: 'CAMEL-AI Ambassador', value: 'Official', color: '#00D9FF' },
    { icon: Briefcase, label: 'Multi-Agent Systems', value: '20+', color: '#00FF88' },
    { icon: Code2, label: 'Tech Stack', value: '15+', color: '#FF00FF' },
    { icon: Users, label: 'Hackathon Winner', value: '2025', color: '#FFD700' }
  ];

  const skills = [
    { name: 'Data Science & Analytics', level: 92 },
    { name: 'Machine Learning Pipelines', level: 90 },
    { name: 'Generative AI, RAG & Agents', level: 93 },
    { name: 'Python, SQL & Delivery', level: 87 }
  ];

  const achievements = [
    { icon: Terminal, text: '200+ DSA problems solved' },
    { icon: Globe, text: 'CAMEL-AI global community contributor' },
    { icon: Calendar, text: 'Available for AI freelance projects' },
    { icon: Sparkles, text: 'Building practical LLM applications' }
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      // Reduced py-24 to py-16 for less vertical spacing
      className="relative py-16 px-6 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, var(--bg-primary) 0%, rgba(0, 217, 255, 0.02) 50%, var(--bg-primary) 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Animated Background Elements (Scaled down slightly) */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-20 left-10 w-56 h-56 rounded-full opacity-20 animate-pulse"
          style={{
            background: 'radial-gradient(circle, var(--brand-primary) 0%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'float 8s ease-in-out infinite'
          }}
        />
        <div
          className="absolute bottom-20 right-10 w-72 h-72 rounded-full opacity-20 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #00FF88 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto w-full relative z-10">
        {/* Section Title - Reduced margin and font size */}
        <div className={`text-center mb-12 ${isVisible ? 'animate-fade-down' : 'opacity-0'}`}>
          <div className="inline-block mb-2">
            <span
              className="text-xs font-bold tracking-[0.3em] uppercase"
              style={{
                color: 'var(--brand-primary)',
                letterSpacing: '0.3em'
              }}
            >
              Get To Know
            </span>
          </div>
          {/* Reduced from text-5xl/7xl to text-4xl/6xl */}
          <h2
            className="text-4xl md:text-6xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--text-primary) 0%, var(--brand-primary) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}
          >
            About Me
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div
              className="h-[2px] w-10"
              style={{
                background: 'linear-gradient(90deg, transparent, var(--brand-primary))'
              }}
            />
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: 'var(--brand-primary)' }}
            />
            <div
              className="h-[2px] w-10"
              style={{
                background: 'linear-gradient(90deg, var(--brand-primary), transparent)'
              }}
            />
          </div>
        </div>

        {/* Content Grid - Reduced gap from 12 to 10 */}
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left: Image & Quick Info */}
          <div className="lg:col-span-2 space-y-5">
            <div
              className={`${isVisible ? 'animate-fade-right' : 'opacity-0'}`}
              style={{ animationDelay: '0.2s' }}
            >
              {/* Reduced max-width to 260px for a smaller image */}
              <div
                ref={imageRef}
                className="relative group max-w-[260px] mx-auto"
                onMouseMove={(e) => {
                  const rect = imageRef.current.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
                  const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
                  imageRef.current.style.transform = `perspective(1000px) rotateY(${x}deg) rotateX(${-y}deg)`;
                }}
                onMouseLeave={() => {
                  imageRef.current.style.transform = 'perspective(1000px) rotateY(0) rotateX(0)';
                }}
                style={{ transformStyle: 'preserve-3d', transition: 'transform 0.3s ease' }}
              >
                {/* Profile Image Container */}
                <div className="relative rounded-xl overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.1), rgba(0, 255, 136, 0.1))',
                      pointerEvents: 'none'
                    }}
                  />
                  <img
                    src="/tushar-profile.jpeg"
                    alt="Tushar Singh"
                    className="w-full h-auto rounded-xl"
                    style={{
                      boxShadow: '0 15px 30px rgba(0, 217, 255, 0.15)',
                      border: '2px solid rgba(0, 217, 255, 0.3)'
                    }}
                  />
                  {/* Animated Border */}
                  <div
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    style={{
                      background: 'conic-gradient(from 0deg, transparent, var(--brand-primary), transparent)',
                      padding: '2px',
                      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      maskComposite: 'exclude',
                      animation: 'spin 3s linear infinite',
                      opacity: 0.5
                    }}
                  />
                </div>

                {/* Floating Badge - Smaller size */}
                <div
                  className="absolute -top-3 -right-3 glass-strong rounded-full p-2.5 animate-bounce"
                  style={{
                    background: 'linear-gradient(135deg, var(--brand-primary), #00FF88)',
                    boxShadow: '0 8px 20px rgba(0, 217, 255, 0.5)'
                  }}
                >
                  <Award size={18} color="white" />
                </div>
              </div>
            </div>

            {/* Quick Achievements - Smaller text and padding */}
            <div
              className={`space-y-2.5 max-w-[280px] mx-auto ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: '0.4s' }}
            >
              {achievements.map((achievement, index) => {
                const Icon = achievement.icon;
                return (
                  <div
                    key={index}
                    className="glass-strong p-2.5 rounded-md flex items-center gap-3 hover-lift-subtle"
                    style={{
                      animationDelay: `${0.5 + index * 0.1}s`,
                      borderLeft: '2px solid var(--brand-primary)'
                    }}
                  >
                    <Icon size={16} style={{ color: 'var(--brand-primary)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                      {achievement.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Bio & Stats */}
          <div className="lg:col-span-3 space-y-6">
            {/* Main Bio - Reduced text sizes */}
            <div
              className={`${isVisible ? 'animate-fade-left' : 'opacity-0'}`}
              style={{ animationDelay: '0.3s' }}
            >
              {/* Heading reduced to text-2xl/3xl */}
              <h3
                className="text-2xl md:text-3xl font-bold mb-4"
                style={{
                  background: 'linear-gradient(90deg, var(--text-primary), var(--brand-primary))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Data Scientist & Generative AI Engineer
              </h3>
              
              {/* Paragraphs reduced to text-sm/base */}
              <div className="space-y-3">
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  I am a <span className="font-semibold" style={{ color: 'var(--brand-primary)' }}>
                    CAMEL-AI Ambassador
                  </span> with experience in machine learning and building intelligent
                  applications using modern AI workflows.
                </p>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  My focus areas include RAG pipelines, multi-agent systems, and LLM-powered
                  solutions with Python, SQL, LangChain, and CAMEL-AI. I enjoy turning AI
                  ideas into production-ready products.
                </p>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Proud winner of <span className="font-semibold" style={{ color: '#FFD700' }}>
                    IEEE Hackathon 2025
                  </span>, currently pursuing BCA and continuously improving across
                  machine learning, agent architectures, and scalable AI engineering.
                </p>
              </div>
            </div>

            {/* Animated Skills - Smaller Labels */}
            <div
              className={`${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: '0.5s' }}
            >
              <h4
                className="text-lg font-bold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                Core Expertise
              </h4>
              <div className="space-y-3">
                {skills.map((skill, index) => (
                  <div
                    key={index}
                    className="relative"
                    onMouseEnter={() => setActiveSkill(index)}
                  >
                    <div className="flex justify-between mb-1.5">
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{skill.name}</span>
                      <span
                        className="text-sm font-bold"
                        style={{ color: activeSkill === index ? 'var(--brand-primary)' : 'var(--text-muted)' }}
                      >
                        {skill.level}%
                      </span>
                    </div>
                    <div
                      className="h-1.5 rounded-full overflow-hidden"
                      style={{ background: 'rgba(255, 255, 255, 0.05)' }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-1000 ease-out"
                        style={{
                          width: isVisible ? `${skill.level}%` : '0%',
                          background: activeSkill === index
                            ? 'linear-gradient(90deg, var(--brand-primary), #00FF88)'
                            : 'linear-gradient(90deg, rgba(0, 217, 255, 0.5), rgba(0, 255, 136, 0.5))',
                          boxShadow: activeSkill === index ? '0 0 15px var(--brand-primary)' : 'none',
                          animationDelay: `${0.7 + index * 0.1}s`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Grid - More compact */}
            <div
              className={`grid grid-cols-2 gap-3 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: '0.6s' }}
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className="glass-strong p-4 rounded-lg hover-lift group relative overflow-hidden"
                    style={{
                      animationDelay: `${0.7 + index * 0.1}s`,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Hover Gradient */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: `linear-gradient(135deg, ${stat.color}20, transparent)`
                      }}
                    />
                    
                    <Icon
                      size={24} // Smaller Icon
                      className="mb-2 group-hover:scale-110 transition-transform duration-300"
                      style={{ color: stat.color }}
                    />
                    <p
                      className="text-xl font-bold mb-0.5" // Smaller text
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {stat.value}
                    </p>
                    <p
                      className="text-xs" // Smaller label
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(10px) translateX(-10px); }
          75% { transform: translateY(-10px) translateX(20px); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-fade-down {
          animation: fadeDown 0.8s ease-out forwards;
        }

        .animate-fade-up {
          animation: fadeUp 0.8s ease-out forwards;
        }

        .animate-fade-left {
          animation: fadeLeft 0.8s ease-out forwards;
        }

        .animate-fade-right {
          animation: fadeRight 0.8s ease-out forwards;
        }

        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeLeft {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fadeRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .hover-lift {
          transition: all 0.3s ease;
        }

        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 217, 255, 0.2);
        }

        .hover-lift-subtle {
          transition: all 0.3s ease;
        }

        .hover-lift-subtle:hover {
          transform: translateX(3px);
          box-shadow: 0 8px 20px rgba(0, 217, 255, 0.1);
        }

        .glass-strong {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </section>
  );
};

export default About;